import React from "react";
import { useTranslation } from "react-i18next";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import { Accordion, AccordionGroup, Checkbox, Chip } from "@outpave/ds";
import type {
  ApiKeyPermissionCatalogue,
  ApiKeyPermissionOption,
} from "hooks/useApiKeys";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  apiKeyGroupLabelKey,
  apiKeyPermissionHelpKey,
  canTogglePermission,
  getGroupOptions,
  getGroupPermissions,
  getGroupState,
  togglePermission,
  toggleGroup,
} from "utils/apiKeys/apiKeyPermissions";

interface ApiKeyPermissionsFieldProps {
  catalogue: ApiKeyPermissionCatalogue;
  selected: string[];
  onSelectedChange: (next: string[]) => void;
  disabled?: boolean;
}

const secondaryText = { color: "text.secondary" } as const;
const lockedText = { color: "text.disabled" } as const;

const ApiKeyPermissionsField: React.FC<ApiKeyPermissionsFieldProps> = ({
  catalogue,
  selected,
  onSelectedChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const renderPermission = (option: ApiKeyPermissionOption) => {
    const locked = disabled || !canTogglePermission(option, selected);
    return (
      <FormControlLabel
        key={option.name}
        sx={{ alignItems: "flex-start" }}
        control={
          <Checkbox
            checked={selected.includes(option.name)}
            disabled={locked}
            onChange={() =>
              onSelectedChange(togglePermission(option.name, selected))
            }
          />
        }
        label={
          <Stack gap="var(--space-2)">
            <Inline gap="var(--space-2)">
              <Typography
                variant="subtitle2"
                sx={locked ? lockedText : undefined}
              >
                {option.name}
              </Typography>
              {option.sensitive && (
                <Chip tone="warning" size="sm">
                  {t("Sensitive")}
                </Chip>
              )}
            </Inline>
            <Typography
              variant="body2"
              sx={locked ? lockedText : secondaryText}
            >
              {t(apiKeyPermissionHelpKey(option.name), { defaultValue: "" })}
            </Typography>
          </Stack>
        }
      />
    );
  };

  return (
    <AccordionGroup framed>
      {catalogue.groups.map((group) => {
        const options = getGroupOptions(group, catalogue.permissions);
        const groupPermissions = getGroupPermissions(
          group,
          catalogue.permissions,
        );
        const state = getGroupState(groupPermissions, selected);
        const held = options.filter((option) =>
          selected.includes(option.name),
        ).length;
        return (
          <Accordion
            key={group.id}
            title={t(apiKeyGroupLabelKey(group.id), { defaultValue: group.id })}
            description={t("ApiKeyPresetSelectedCount", {
              held,
              total: options.length,
            })}
          >
            {options.length > 1 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={groupPermissions.length > 0 && state === "all"}
                    indeterminate={state === "some"}
                    disabled={disabled || groupPermissions.length === 0}
                    onChange={() =>
                      onSelectedChange(toggleGroup(groupPermissions, selected))
                    }
                  />
                }
                label={t("Select all")}
              />
            )}
            <Stack
              gap="var(--space-5)"
              sx={{
                paddingInlineStart:
                  options.length > 1 ? "var(--space-7)" : undefined,
              }}
            >
              {options.map(renderPermission)}
            </Stack>
          </Accordion>
        );
      })}
    </AccordionGroup>
  );
};

export default ApiKeyPermissionsField;
